import * as React from "react";

/**
 * @see https://github.com/radix-ui/primitives/blob/main/packages/react/use-callback-ref/src/useCallbackRef.tsx
 */

/**
 * Converts a callback to a stable ref-based function that never changes identity.
 *
 * @description
 * Stores the latest `callback` in a ref and returns a memoized wrapper that delegates to it.
 * This avoids triggering re-renders when the callback is passed as a prop, and avoids
 * re-executing effects when listed as a dependency.
 *
 * Based on Radix UI's `useCallbackRef` primitive.
 *
 * @template T - The callback function type.
 * @param callback - The callback to stabilize. May be `undefined`.
 * @returns A stable function with the same signature as `callback`.
 *
 * @see https://github.com/radix-ui/primitives/blob/main/packages/react/use-callback-ref/src/useCallbackRef.tsx
 */
function useCallbackRef<T extends (...args: never[]) => unknown>(
  callback: T | undefined,
): T {
  const callbackRef = React.useRef(callback);

  React.useEffect(() => {
    callbackRef.current = callback;
  });

  return React.useMemo(
    () => ((...args) => callbackRef.current?.(...args)) as T,
    [],
  );
}

export { useCallbackRef };
